import { useEffect, useRef, useState } from 'react';
import { Box, Button, Flex, Text } from '@chakra-ui/react';
import { ChevronDown, Check } from 'lucide-react';
import type { CommunicationEvent } from '../useCommunicationPage';

type Props = {
  events: CommunicationEvent[];
  selectedId: string | null;
  onSelect: (eventId: string) => void;
};

/**
 * Which event the page is showing.
 *
 * With one event there is nothing to choose, so it renders as plain text -- a dropdown
 * with a single option is noise. A native menu is avoided because the trigger has to sit
 * inside the header pill row on touch.
 */
export function EventSelector({ events, selectedId, onSelect }: Props) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const selected = events.find((e) => e.id === selectedId) ?? null;

  useEffect(() => {
    if (!open) return;
    const onDocClick = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node))
        setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onDocClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDocClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  if (events.length === 0) return null;

  if (events.length === 1) {
    return (
      <Flex
        align="center"
        h="36px"
        px={3}
        maxW="180px"
        minW={0}
        borderRadius="full"
        borderWidth={1}
        borderColor="volyx.borderLight"
        bg="volyx.bg"
      >
        <Text fontSize="sm" color="volyx.textSecondary" lineClamp={1}>
          {selected?.name ?? events[0].name}
        </Text>
      </Flex>
    );
  }

  return (
    <Box position="relative" ref={ref} minW={0}>
      <Button
        type="button"
        variant="outline"
        size="sm"
        h="36px"
        px={3}
        maxW="180px"
        borderRadius="full"
        borderWidth={1}
        // Reads as a control rather than as a label, and matches the pill title beside it.
        borderColor={open ? 'volyx.action' : 'volyx.borderLight'}
        bg="volyx.bg"
        color="volyx.primary"
        _hover={{ borderColor: 'volyx.mediumGray' }}
        aria-haspopup="listbox"
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
      >
        <Text fontSize="sm" fontWeight="semibold" lineClamp={1} minW={0}>
          {selected?.name ?? 'Select event'}
        </Text>
        <Box
          display="flex"
          transform={open ? 'rotate(180deg)' : 'rotate(0deg)'}
          transition="transform 0.18s ease-out"
        >
          <ChevronDown size={16} />
        </Box>
      </Button>

      {/* Kept mounted so closing animates too -- unmounting on close would cut the fade
          off. visibility (not display) is what keeps it out of the tab order and off the
          accessibility tree while still being transitionable. */}
      <Box
        role="listbox"
        aria-hidden={!open}
        position="absolute"
        top="calc(100% + 4px)"
        right={0}
        zIndex={20}
        minW="220px"
        maxH="320px"
        overflowY="auto"
        bg="volyx.bg"
        borderRadius="lg"
        borderWidth={1}
        borderColor="volyx.borderLight"
        boxShadow="0 8px 24px rgba(0, 0, 0, 0.12)"
        py={1}
        opacity={open ? 1 : 0}
        // Grows out of the button it hangs from, rather than fading in place.
        transformOrigin="top right"
        transform={
          open ? 'translateY(0) scale(1)' : 'translateY(-6px) scale(0.96)'
        }
        visibility={open ? 'visible' : 'hidden'}
        pointerEvents={open ? 'auto' : 'none'}
        // visibility is delayed on the way out so the fade is not cut short, and snaps
        // back instantly on the way in so the first frame is already hit-testable.
        transition={
          open
            ? 'opacity 0.18s ease-out, transform 0.18s ease-out, visibility 0s'
            : 'opacity 0.14s ease-in, transform 0.14s ease-in, visibility 0s 0.14s'
        }
      >
        {events.map((event) => (
          <Flex
            key={event.id}
            role="option"
            aria-selected={event.id === selectedId}
            tabIndex={open ? 0 : -1}
            align="center"
            justify="space-between"
            gap={2}
            px={3}
            py={2}
            cursor="pointer"
            _hover={{ bg: 'volyx.lightGray' }}
            onClick={() => {
              onSelect(event.id);
              setOpen(false);
            }}
            onKeyDown={(e) => {
              if (e.key !== 'Enter' && e.key !== ' ') return;
              e.preventDefault();
              onSelect(event.id);
              setOpen(false);
            }}
          >
            <Text fontSize="sm" color="gray.800" lineClamp={1}>
              {event.name}
            </Text>
            {event.id === selectedId ? (
              <Box color="volyx.action" flexShrink={0}>
                <Check size={16} />
              </Box>
            ) : null}
          </Flex>
        ))}
      </Box>
    </Box>
  );
}
